/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import CaptureHeader from '../CaptureHeader';
import CachedImage from '../CachedImage';
import NotesButton from './NotesButton';
import GlobalInstructionButton from './GlobalInstructionButton';
import ReferenceTooltip from './ReferenceTooltip';
import PhotoGallery from './PhotoGallery';
import FullScreenGallery from './FullScreenGallery';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';
import './capture.css';

// previewUrl covers offline/not-yet-synced photos, which have no url/truVideoUrl yet.
const getPhotoUrl = (photo) => photo?.uploadResult?.fileUrl || photo?.url || photo?.previewUrl || photo?.truVideoUrl;

const MultiPhotoReview = ({
  assignment,
  workflow,
  branding,
  item,
  stepNumber,
  totalSteps,
  photos,
  onAddMore,
  onRetakePhoto,
  onDeletePhoto,
  onBack,
  onNext,
  token,
  onOpenNotes,
  onSaveAndExit,
}) => {
  const currentTheme = branding?.theme || 'light';
  const maxPhotos = Number(item.maxPhotos ?? 1);
  const canAddMore = photos.length < maxPhotos;
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [deletingPhotoKey, setDeletingPhotoKey] = useState(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);

  // Separate tooltip state/refs for the landscape sidebar's identity block —
  // both the portrait overlay header and the landscape sidebar header are
  // mounted simultaneously (toggled via CSS, not conditional rendering), so
  // they can't share a single ref/trigger.
  const [showLandscapeTooltip, setShowLandscapeTooltip] = useState(false);
  const [showLandscapeNameTooltip, setShowLandscapeNameTooltip] = useState(false);
  const landscapeTooltipTriggerRef = useRef(null);
  const landscapeNameTooltipTriggerRef = useRef(null);

  // Clamp activePhotoIndex when photos array shrinks (e.g. after a deletion)
  useEffect(() => {
    if (photos.length > 0 && activePhotoIndex >= photos.length) {
      setActivePhotoIndex(photos.length - 1);
    }
  }, [photos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive a clamped index/photo for render, since state clamping above only
  // takes effect on the next render after a deletion shrinks the array.
  const currentPhotoIndex = Math.min(activePhotoIndex, photos.length - 1);
  const currentPhoto = photos[currentPhotoIndex];

  const handleDeleteClick = async (photo, key) => {
    if (deletingPhotoKey) return;
    setDeletingPhotoKey(key);
    try {
      await onDeletePhoto(photo);
    } finally {
      setDeletingPhotoKey(null);
    }
  };

  return createPortal(
    <div data-theme={currentTheme} className="fixed inset-0 z-[9999] bg-base-100 flex flex-col capture-container landscape-root">

      {viewerOpen && photos.length > 0 && (
        <FullScreenGallery
          items={photos}
          initialIndex={currentPhotoIndex}
          onClose={() => setViewerOpen(false)}
          onIndexChange={setActivePhotoIndex}
          renderItem={(photo) => (
            <CachedImage
              key={getPhotoUrl(photo)}
              token={token}
              src={getPhotoUrl(photo)}
              alt="Full view"
              onClick={e => e.stopPropagation()}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
        />
      )}

      {/* Portrait: dark overlay header on top of the photo — hidden in landscape */}
      <div className="landscape-portrait-multiphoto-header absolute top-0 left-3 right-3 bg-black/60 p-4 pt-0 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg">
        <div className="w-full text-left">

          <div className="mb-3">
            <CaptureHeader
              expiresAt={assignment?.expiresAt}
              brandLogo={workflow?.branding?.logoUrl}
              branding={branding}
              className="pt-0 pb-0 px-0"
              type="overlay"
              token={token}
              onSaveAndExit={onSaveAndExit}
            />
          </div>

          {/* Submitter Identity */}
          <div className="flex flex-col mb-0 gap-1">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0 flex-1">
                <h1
                  title={assignment?.submitterName}
                  ref={nameTooltipTriggerRef}
                  className="text-white text-[20px] font-bold leading-none drop-shadow-md truncate max-w-[140px]"
                  onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
                >
                  {assignment?.submitterName || 'Valued Customer'}
                </h1>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showNameTooltip}
                  onClose={() => setShowNameTooltip(false)}
                  theme={currentTheme}
                  triggerRef={nameTooltipTriggerRef}
                  type="name"
                />
              </div>
              {onOpenNotes && <NotesButton onClick={onOpenNotes} overlay />}
            </div>
            <div className="flex flex-col text-[13px] text-white/90 gap-1 drop-shadow-md">
              <div className="whitespace-nowrap flex justify-between items-start w-full">
                <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
                <span className="shrink-0 inline-block text-right">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-white underline decoration-dashed focus:outline-none text-right custom-line-break"
                  >
                    {assignment?.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-2 mb-4 w-full">
            <span className="text-[13px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepNumber} of {totalSteps}</span>
            <div className="flex-1 flex h-[5px] gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-[2px] transition-all duration-500 ${i < stepNumber ? 'bg-primary' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-white text-[20px] font-bold tracking-tight drop-shadow-md leading-snug max-w-[75%] break-words truncate">
              {item.description}
            </p>
            {item.required && (
              <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm">
                Required
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── LEFT SIDEBAR (landscape only) — mirrors Review.jsx's sidebar ── */}
      <div className="landscape-sidebar w-full">
        <div className="landscape-sidebar-action hidden flex-col gap-3 p-3">
          <CaptureHeader
            expiresAt={assignment?.expiresAt}
            brandLogo={workflow?.branding?.logoUrl}
            branding={branding}
            className="pt-0 pb-0 px-0"
            type="before-capture"
            token={token}
            onSaveAndExit={onSaveAndExit}
          />

          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-start gap-2">
              <div className="relative min-w-0">
                <h2
                  title={assignment?.submitterName}
                  ref={landscapeNameTooltipTriggerRef}
                  className="text-[20px] font-bold text-base-content leading-none tracking-tight truncate cursor-pointer transition-opacity hover:opacity-80 active:opacity-70"
                  onClick={() => setShowLandscapeNameTooltip(!showLandscapeNameTooltip)}
                >
                  {assignment?.submitterName || 'Valued Customer'}
                </h2>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showLandscapeNameTooltip}
                  onClose={() => setShowLandscapeNameTooltip(false)}
                  theme={currentTheme}
                  triggerRef={landscapeNameTooltipTriggerRef}
                  type="name"
                  className="tooltip-for-landscap-review"
                />
              </div>
              {onOpenNotes && <NotesButton onClick={onOpenNotes} />}
            </div>
            <div className="shrink max-w-full overflow-visible text-[14px]">
              <div className="relative flex flex-wrap justify-between items-start custom-line-break w-full">
                <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:{" "}</span>
                <span className="inline-block text-right shrink max-w-full break-all">
                  <button
                    type="button"
                    ref={landscapeTooltipTriggerRef}
                    onClick={() => setShowLandscapeTooltip(!showLandscapeTooltip)}
                    className="underline decoration-dashed text-primary custom-line-break focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {assignment?.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showLandscapeTooltip}
                  onClose={() => setShowLandscapeTooltip(false)}
                  theme={currentTheme}
                  triggerRef={landscapeTooltipTriggerRef}
                  type="reference"
                  className="tooltip-for-landscap-review"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full">
            <span className="text-[13px] font-bold text-base-content/60 whitespace-nowrap">Step {stepNumber} of {totalSteps}</span>
            <div className="flex-1 flex h-[5px] gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-full transition-all duration-500 ${i < stepNumber ? 'bg-primary shadow-sm' : 'bg-base-content/10'}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-[18px] font-bold tracking-tight leading-snug text-base-content custom-line-break">
              {item.description}
            </p>
            {item.required && (
              <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm shrink-0">
                Required
              </span>
            )}
          </div>
        </div>

        {/* Landscape-only: Back + instruction icon + Next, pinned to bottom of sidebar */}
        <div className="landscape-sidebar-action hidden mt-auto items-end justify-between gap-3 p-3">
          <div className="flex flex-col items-start gap-2">
            <GlobalInstructionButton
              globalInstructionUrl={workflow?.globalInstructionUrl}
              token={token}
              className="!w-11 !h-11 shrink-0"
            />
            <button
              onClick={onBack}
              className="w-[90px] h-[44px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer active:scale-95 transition-transform"
            >
              Back
            </button>
          </div>
          <button
            onClick={onNext}
            className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer active:scale-95 transition-transform"
          >
            Next
          </button>
        </div>
      </div>

      {/* ── RIGHT MAIN PANEL (landscape) / Scrollable content (portrait) ──
          Single mounted copy — only the wrapper's padding changes per
          orientation (via .multiphoto-content-panel in capture.css), so the
          gallery/actions below don't run as two live React trees at once. */}
      {/* min-h-0 on both: without it a flex child never shrinks below its
          content height, so on short screens the panel grew past the
          viewport (under the fixed bottom bar) instead of scrolling. */}
      <div className="landscape-main flex-1 min-h-0 max-w-full flex flex-col">
        <div className="multiphoto-content-panel flex-1 min-h-0 space-y-5 overflow-y-auto overscroll-contain w-full max-w-full pt-[220px] pb-[160px] px-4">

          {/* Photo count */}
          <p className="text-[14px] text-base-content/70">
            <span className="font-bold text-base-content">{photos.length}</span> of <span className="font-bold text-base-content">{maxPhotos}</span> photos captured
          </p>

          {/* Photo gallery — swipeable carousel, matches Review.jsx multi-photo card */}
          {photos.length > 0 && currentPhoto && (
            <div className="animate-fade-in">
              <div className="relative rounded-[8px] overflow-hidden bg-black aspect-video border border-base-300 shadow-md">
                <PhotoGallery
                  photos={photos}
                  activeIndex={currentPhotoIndex}
                  onIndexChange={setActivePhotoIndex}
                  renderSlide={(photo) => (
                    <CachedImage
                      key={getPhotoUrl(photo)}
                      token={token}
                      src={getPhotoUrl(photo)}
                      alt={`Photo ${currentPhotoIndex + 1}`}
                      className="w-full h-full object-contain"
                    />
                  )}
                  canPreview
                  onPreview={() => setViewerOpen(true)}
                />
              </div>

              {/* Actions for the currently visible photo */}
              <div className="flex justify-center gap-6 mt-3">
                <button
                  type="button"
                  onClick={() => onRetakePhoto(currentPhoto, currentPhotoIndex)}
                  className="min-w-[108px] h-[42px] bg-black text-white font-bold text-[17px] rounded-[8px] border-2 border-black active:scale-[0.98] transition-all shadow-md flex items-center justify-center whitespace-nowrap px-3"
                >
                  Retake
                </button>
                {onDeletePhoto && (photos.length > 1 || !item.required) && (
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(currentPhoto, currentPhoto.mediaFileId ?? currentPhoto.localId ?? currentPhotoIndex)}
                    disabled={deletingPhotoKey !== null}
                    className="min-w-[108px] h-[42px] bg-error text-error-content font-bold text-[17px] rounded-[8px] border-2 border-error active:scale-[0.98] transition-all shadow-md flex items-center justify-center whitespace-nowrap px-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingPhotoKey === (currentPhoto.mediaFileId ?? currentPhoto.localId ?? currentPhotoIndex) ? (
                      <span className="w-4 h-4 border-2 border-error-content/40 border-t-error-content rounded-full animate-spin" />
                    ) : (
                      'Delete'
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Add more images */}
          {canAddMore && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={onAddMore}
                className="w-[240px] h-[42px] rounded-[8px] bg-base-100 text-primary border-2 border-primary font-bold text-[17px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform whitespace-nowrap"
              >
                Add more images
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Portrait: Bottom Action Bar — hidden in landscape (buttons live in the sidebar instead) */}
      <div className="landscape-hide-fixed-bar fixed bottom-0 left-0 right-0 p-4 px-3 bg-base-100/95 backdrop-blur-sm rounded-t-[20px] z-30 shadow-[0_-4px_10px_rgba(0,0,0,0.1)] border-t border-base-300/50">
        <div className="max-w-full mx-auto flex items-end justify-between md:justify-center lg:justify-center gap-10">
          <div className="flex flex-col items-start gap-2">
            <GlobalInstructionButton
              globalInstructionUrl={workflow?.globalInstructionUrl}
              token={token}
              className="!w-11 !h-11 shrink-0"
            />
            <button
              onClick={onBack}
              className="w-[90px] h-[44px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer active:scale-95 transition-transform"
            >
              Back
            </button>
          </div>
          <button
            onClick={onNext}
            className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer active:scale-95 transition-transform"
          >
            Next
          </button>
        </div>
      </div>


    </div>,
    document.body
  );
};

MultiPhotoReview.propTypes = {
  assignment: PropTypes.object,
  workflow: PropTypes.object,
  branding: PropTypes.object,
  item: PropTypes.shape({
    description: PropTypes.string,
    required: PropTypes.bool,
    maxPhotos: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,
  photos: PropTypes.array,
  onAddMore: PropTypes.func,
  onRetakePhoto: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  token: PropTypes.string,
  onOpenNotes: PropTypes.func,
  onSaveAndExit: PropTypes.func,
};

export default MultiPhotoReview;
